import React, { lazy, Suspense, useEffect, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ComparisonProvider } from "@/contexts/ComparisonContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import LandlordRoute from "./components/LandlordRoute";
import { SEOHead } from "@/components/SEOHead";
import { SkipToContent } from "@/components/SkipToContent";
import lazyWithRetry from "@/lib/lazyWithRetry";
import * as Sentry from "@sentry/react";
import { initGA, logPageView } from "@/utils/analytics";
import { AlertTriangle } from "lucide-react";
import GlobalBackBar from "@/components/GlobalBackBar";
import { RouteWrapper } from "@/components/RouteWrapper";

const BottomNav = lazy(() => import("./components/BottomNav"));

// Lazy load all page components for better code splitting
// Use lazyWithRetry for critical pages to handle chunk-load failures gracefully
const Index = lazyWithRetry(() => import("./pages/Index"));
const Search = lazyWithRetry(() => import("./pages/Search"));
const Landlords = lazyWithRetry(() => import("./pages/Landlords"));
const About = lazyWithRetry(() => import("./pages/About"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const StudentDashboard = lazyWithRetry(() => import("./pages/StudentDashboard"));

const Messages = lazyWithRetry(() => import("./pages/Messages"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentCancelled = lazy(() => import("./pages/PaymentCancelled"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PersonalityQuiz = lazyWithRetry(() => import("./pages/PersonalityQuiz"));
const BuddySystem = lazyWithRetry(() => import("./pages/BuddySystem"));
const HorrorStories = lazyWithRetry(() => import("./pages/HorrorStories"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Contact = lazy(() => import("./pages/Contact"));
const FAQ = lazy(() => import("./pages/FAQ"));
const ListProperty = lazyWithRetry(() => import("./pages/ListProperty"));
const AdvancedListProperty = lazyWithRetry(() => import("./pages/AdvancedListProperty"));
const AdvancedSearch = lazyWithRetry(() => import("./pages/AdvancedSearch"));

const PriceAlerts = lazy(() => import("./pages/PriceAlerts"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const HostelDetailNew = lazy(() => import("./pages/HostelDetailNew"));
const Support = lazy(() => import("./pages/Support"));
const LandlordOnboarding = lazy(() => import("./components/LandlordOnboarding"));
const ListPropertyGateway = lazy(() => import("./pages/ListPropertyGateway"));
const MyProperties = lazy(() => import("./pages/MyProperties"));
const RoomChat = lazy(() => import("./pages/RoomChat"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Referrals = lazy(() => import("./pages/Referrals"));

// Admin Portal
const AdminLayout = lazyWithRetry(() => import("./layouts/AdminLayout").then(m => ({ default: m.AdminLayout })));
const AdminDashboardPage = lazyWithRetry(() => import("./pages/admin/AdminDashboardPage").then(m => ({ default: m.AdminDashboardPage })));
const AdminPaymentsPage = lazyWithRetry(() => import("./pages/admin/AdminPaymentsPage").then(m => ({ default: m.AdminPaymentsPage })));
const AdminSettlementsPage = lazyWithRetry(() => import("./pages/admin/AdminSettlementsPage").then(m => ({ default: m.AdminSettlementsPage })));
const AdminBookingsPage = lazyWithRetry(() => import("./pages/admin/AdminBookingsPage").then(m => ({ default: m.AdminBookingsPage })));
const AdminUsersPage = lazyWithRetry(() => import("./pages/admin/AdminUsersPage").then(m => ({ default: m.AdminUsersPage })));
const AdminPropertiesPage = lazyWithRetry(() => import("./pages/admin/AdminPropertiesPage").then(m => ({ default: m.AdminPropertiesPage })));
const AdminReportsPage = lazyWithRetry(() => import("./pages/admin/AdminReportsPage").then(m => ({ default: m.AdminReportsPage })));
const AdminSecurityPage = lazyWithRetry(() => import("./pages/admin/AdminSecurityPage").then(m => ({ default: m.AdminSecurityPage })));
const AdminAuditLogPage = lazyWithRetry(() => import("./pages/admin/AdminAuditLogPage").then(m => ({ default: m.AdminAuditLogPage })));
const AdminPrelaunchPage = lazyWithRetry(() => import("./pages/admin/AdminPrelaunchPage").then(m => ({ default: m.AdminPrelaunchPage })));
const AdminSettingsPage = lazyWithRetry(() => import("./pages/admin/AdminSettingsPage").then(m => ({ default: m.AdminSettingsPage })));
const RecommendedHostels = lazyWithRetry(() => import("./components/RecommendedHostels").then(m => ({ default: m.RecommendedHostels })));
const AnalyticsDashboard = lazyWithRetry(() => import("./pages/AnalyticsDashboard"));
const CompareHostels = lazyWithRetry(() => import("./pages/CompareHostels"));
const Notifications = lazyWithRetry(() => import("./pages/Notifications").then(m => ({ default: m.Notifications })));

// Landlord portal
const LandlordLayout = lazyWithRetry(() => import("./layouts/LandlordLayout").then(m => ({ default: m.LandlordLayout })));
const LandlordOverview = lazyWithRetry(() => import("./pages/landlord/LandlordOverview").then(m => ({ default: m.LandlordOverview })));
const LandlordProperties = lazyWithRetry(() => import("./pages/landlord/LandlordProperties").then(m => ({ default: m.LandlordProperties })));
const LandlordBookings = lazyWithRetry(() => import("./pages/landlord/LandlordBookings").then(m => ({ default: m.LandlordBookings })));
const LandlordMaintenance = lazyWithRetry(() => import("./pages/landlord/LandlordMaintenance").then(m => ({ default: m.LandlordMaintenance })));
const LandlordExpenses = lazyWithRetry(() => import("./pages/landlord/LandlordExpenses").then(m => ({ default: m.LandlordExpenses })));
const LandlordLeases = lazyWithRetry(() => import("./pages/landlord/LandlordLeases").then(m => ({ default: m.LandlordLeases })));
const LandlordAnnouncements = lazyWithRetry(() => import("./pages/landlord/LandlordAnnouncements").then(m => ({ default: m.LandlordAnnouncements })));
const LandlordTenants = lazyWithRetry(() => import("./pages/landlord/LandlordTenants").then(m => ({ default: m.LandlordTenants })));
const LandlordAnalytics = lazyWithRetry(() => import("./pages/landlord/LandlordAnalytics").then(m => ({ default: m.LandlordAnalytics })));
const LandlordRoomManagement = lazyWithRetry(() => import("./pages/landlord/LandlordRoomManagement").then(m => ({ default: m.LandlordRoomManagement })));
const LandlordOccupancy = lazyWithRetry(() => import("./pages/landlord/LandlordOccupancy").then(m => ({ default: m.LandlordOccupancy })));
const LandlordSubscription = lazyWithRetry(() => import("./pages/landlord/LandlordSubscription").then(m => ({ default: m.LandlordSubscription })));
const LandlordLeads = lazyWithRetry(() => import("./pages/landlord/LandlordLeads").then(m => ({ default: m.LandlordLeads })));
const LandlordPayoutSettings = lazyWithRetry(() => import("./pages/landlord/LandlordPayoutSettings").then(m => ({ default: m.LandlordPayoutSettings })));

const CompareFloatingBar = lazyWithRetry(() => import("./components/CompareFloatingBar").then(m => ({ default: m.CompareFloatingBar })));

// Stale-while-revalidate: keep previous data visible while refetching
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2, // 2 min — data stays "fresh", no refetch on re-mount
      gcTime: 1000 * 60 * 10,   // 10 min — keep unused data in cache
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Component to track page views
const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    logPageView();
  }, [location]);

  return null;
};

/**
 * Auth hydration gate — prevents the entire app tree from rendering
 * (and then re-rendering) while Supabase verifies the session.
 * Includes a 3-second safety timeout so the app never gets stuck loading.
 */
const AuthGate = ({ children }: { children: React.ReactNode }) => {
  const { isHydrated } = useAuth();
  const [forceReady, setForceReady] = useState(false);

  useEffect(() => {
    if (isHydrated) return;
    console.log('[AuthGate] Waiting for auth hydration…');
    const timer = setTimeout(() => {
      console.warn('[AuthGate] Auth hydration timed out after 3s — forcing render');
      setForceReady(true);
    }, 3000);
    return () => clearTimeout(timer);
  }, [isHydrated]);

  if (!isHydrated && !forceReady) {
    return (
      <div className="min-h-screen bg-background">
        {/* Persistent nav skeleton */}
        <div className="fixed top-0 left-0 right-0 z-50 h-14 md:h-16 bg-background/85 backdrop-blur-2xl border-b border-border/30">
          <div className="container mx-auto px-4 h-full flex items-center">
            <div className="w-8 h-8 bg-primary rounded-xl flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-sm">H</span>
            </div>
            <div className="ml-2.5 h-5 w-28 bg-muted rounded animate-pulse" />
          </div>
        </div>
        {/* Body skeleton */}
        <div className="pt-20 flex items-center justify-center" style={{ minHeight: '60vh' }}>
          <div className="text-center space-y-3">
            <div className="animate-spin rounded-full h-7 w-7 border-2 border-primary border-t-transparent mx-auto" />
            <p className="text-xs text-muted-foreground">Loading…</p>
          </div>
        </div>
      </div>
    );
  }

  if (forceReady && !isHydrated) {
    console.log('[AuthGate] Rendering in guest mode (auth not resolved)');
  }

  return <>{children}</>;
};

// Suspense fallback that doesn't cause full-page flash
const PageFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="text-center space-y-3">
      <div className="animate-spin rounded-full h-7 w-7 border-2 border-primary border-t-transparent mx-auto" />
      <p className="text-xs text-muted-foreground">Loading…</p>
    </div>
  </div>
);

const App = () => {
  useEffect(() => {
    initGA();
  }, []);

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag("boundary", "app-root");
      }}
      fallback={({ error, resetError }: { error: unknown; resetError: () => void }) => {
        const errorMessage = error instanceof Error ? error.message : String(error);

        const isNonCriticalError = errorMessage.includes('WebSocket') || 
          errorMessage.includes('insecure') ||
          errorMessage.includes('realtime') ||
          errorMessage.includes('service worker') ||
          errorMessage.includes('ServiceWorker') ||
          // Browser View Transitions API errors are triggered by the browser itself
          // when layout/viewport changes occur during navigation transitions.
          // They are not actionable from application code and should be silently ignored.
          errorMessage.toLowerCase().includes('view transition') ||
          errorMessage.toLowerCase().includes('skiptransition');
        
        if (isNonCriticalError) {
          resetError();
          return null;
        }

        // Chunk-load / dynamic-import errors: auto-reload once so the browser
        // fetches the latest HTML and correct hashed chunk filenames after a
        // new deployment. The same RELOAD_FLAG used in lazyWithRetry guards
        // against infinite reload loops.
        const RELOAD_FLAG = "chunk-reload-attempted";
        const isChunkError =
          errorMessage.includes("Importing a module script failed") ||
          errorMessage.includes("Failed to fetch dynamically imported module") ||
          errorMessage.includes("error loading dynamically imported module") ||
          errorMessage.includes("Unable to preload CSS for") ||
          errorMessage.toLowerCase().includes("chunk");

        if (isChunkError) {
          const hasReloaded = sessionStorage.getItem(RELOAD_FLAG) === "true";
          if (!hasReloaded) {
            sessionStorage.setItem(RELOAD_FLAG, "true");
            window.location.reload();
            return null;
          }
        }

        // For chunk errors where a reload was already attempted, show a
        // user-friendly prompt to reload manually rather than the raw error.
        const isChunkErrorAfterReload = isChunkError;

        return (
          <div className="min-h-screen flex items-center justify-center bg-background p-4">
            <div className="max-w-md w-full space-y-4 text-center">
              <div className="flex justify-center"><AlertTriangle className="h-12 w-12 text-destructive" /></div>
              <h1 className="text-2xl font-bold">
                {isChunkErrorAfterReload ? "Page failed to load" : "Something went wrong"}
              </h1>
              <p className="text-muted-foreground">
                {isChunkErrorAfterReload
                  ? "A network or caching issue prevented the page from loading. Please try refreshing."
                  : errorMessage || "An unexpected error occurred"}
              </p>
              <div className="flex gap-2 justify-center">
                <button
                  onClick={() => window.location.href = '/'}
                  className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80"
                >
                  Go Home
                </button>
                <button
                  onClick={() => {
                    sessionStorage.removeItem(RELOAD_FLAG);
                    window.location.reload();
                  }}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
                >
                  Refresh Page
                </button>
              </div>
            </div>
          </div>
        );
      }}
    >
      <>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <SkipToContent />
            <AnalyticsTracker />
            <AuthProvider>
              <AuthGate>
                <ComparisonProvider>
                  <GlobalBackBar />
                  <RouteWrapper>
                    <Routes>
                      <Route path="/" element={
                        <>
                          <SEOHead 
                            includeWebsiteSchema 
                            title="Hostel Hunters - Find Your Perfect Student Accommodation in Ghana"
                            description="Discover verified student hostels across Ghana. Hostel Hunters helps you find, compare, and book your perfect accommodation near top universities with ease."
                          />
                          <Index />
                        </>
                      } />
                      <Route path="/search" element={
                        <>
                          <SEOHead
                            title="Search Hostels"
                            description="Find and book verified student hostels across Ghana. Filter by location, price, amenities and more."
                            keywords={['search hostels Ghana', 'find student accommodation', 'hostel booking']}
                          />
                          <Search />
                        </>
                      } />
                      <Route path="/find-hostels" element={<Navigate to="/search" replace />} />
                      <Route path="/advanced-search" element={<AdvancedSearch />} />
                      <Route path="/new-find-hostels" element={<Navigate to="/search" replace />} />
                      
                      <Route path="/dashboard" element={<Navigate to="/student-dashboard" replace />} />
                      <Route path="/student-dashboard" element={
                        <ProtectedRoute requireAuth={true} allowedRoles={['student']}>
                          <StudentDashboard />
                        </ProtectedRoute>
                      } />
                      {/* Landlord Portal - Nested Routes */}
                      <Route path="/landlord-dashboard" element={
                        <LandlordRoute fallbackPath="/">
                          <LandlordLayout />
                        </LandlordRoute>
                      }>
                        <Route index element={<LandlordOverview />} />
                        <Route path="properties" element={<LandlordProperties />} />
                        <Route path="properties/:id/rooms" element={<LandlordRoomManagement />} />
                        <Route path="occupancy" element={<LandlordOccupancy />} />
                        <Route path="bookings" element={<LandlordBookings />} />
                        <Route path="maintenance" element={<LandlordMaintenance />} />
                        <Route path="expenses" element={<LandlordExpenses />} />
                        <Route path="leases" element={<LandlordLeases />} />
                        <Route path="announcements" element={<LandlordAnnouncements />} />
                        <Route path="tenants" element={<LandlordTenants />} />
                        <Route path="analytics" element={<LandlordAnalytics />} />
                        <Route path="leads" element={<LandlordLeads />} />
                        <Route path="subscription" element={<LandlordSubscription />} />
                        <Route path="payouts" element={<LandlordPayoutSettings />} />
                      </Route>
                      <Route path="/messages" element={
                        <ProtectedRoute requireAuth={true}>
                          <Messages />
                        </ProtectedRoute>
                      } />
                      <Route path="/referrals" element={
                        <ProtectedRoute requireAuth={true}>
                          <Referrals />
                        </ProtectedRoute>
                      } />
                      <Route path="/hostel-old/:id" element={<Navigate to="/search" replace />} />
                      <Route path="/payment-success" element={<PaymentSuccess />} />
                      <Route path="/payment-cancelled" element={<PaymentCancelled />} />
                      <Route path="/landlords" element={<Landlords />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/personality-quiz" element={<PersonalityQuiz />} />
                      <Route path="/buddy-system" element={
                        <ProtectedRoute requireAuth={true} allowedRoles={['student']}>
                          <BuddySystem />
                        </ProtectedRoute>
                      } />
                      <Route path="/room-chat" element={
                        <ProtectedRoute requireAuth={true} allowedRoles={['student']}>
                          <RoomChat />
                        </ProtectedRoute>
                      } />
                      <Route path="/horror-stories" element={<HorrorStories />} />
                      <Route path="/auth/callback" element={<AuthCallback />} />
                      <Route path="/email-confirmation" element={<AuthCallback />} />
                      <Route path="/verify-email" element={<VerifyEmail />} />
                      <Route path="/terms-of-service" element={<TermsOfService />} />
                      <Route path="/terms" element={<TermsAndConditions />} />
                      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/support" element={<Support />} />
                      <Route path="/faq" element={<FAQ />} />
                      <Route path="/list-property" element={<ListPropertyGateway />} />
                      <Route path="/list-property-simple" element={
                        <LandlordRoute fallbackPath="/list-property">
                          <ListProperty />
                        </LandlordRoute>
                      } />
                      <Route path="/list-property-advanced" element={
                        <LandlordRoute fallbackPath="/list-property">
                          <AdvancedListProperty />
                        </LandlordRoute>
                      } />
                      <Route path="/my-properties" element={
                        <LandlordRoute fallbackPath="/list-property">
                          <Navigate to="/landlord-dashboard/properties" replace />
                        </LandlordRoute>
                      } />
                      <Route path="/landlord-analytics" element={
                        <LandlordRoute fallbackPath="/list-property">
                          <AnalyticsDashboard />
                        </LandlordRoute>
                      } />
                      <Route path="/compare" element={<CompareHostels />} />
                      <Route path="/price-alerts" element={<PriceAlerts />} />
                      <Route path="/notifications" element={
                        <ProtectedRoute requireAuth={true}>
                          <Notifications />
                        </ProtectedRoute>
                      } />
                      <Route path="/wishlist" element={<Wishlist />} />
                      <Route path="/landlord-onboarding" element={<LandlordRoute><LandlordOnboarding /></LandlordRoute>} />
                      <Route path="/hostel/:id" element={<HostelDetailNew />} />
                      <Route path="/auth" element={
                        <ProtectedRoute requireAuth={false}>
                          <Auth />
                        </ProtectedRoute>
                      } />
                      <Route path="/reset-password" element={<ResetPassword />} />
                      {/* Admin Portal */}
                      <Route path="/admin" element={<AdminLayout />}>
                        <Route index element={<AdminDashboardPage />} />
                        <Route path="analytics" element={<AdminReportsPage />} />
                        <Route path="payments" element={<AdminPaymentsPage />} />
                        <Route path="settlements" element={<AdminSettlementsPage />} />
                        <Route path="bookings" element={<AdminBookingsPage />} />
                        <Route path="users" element={<AdminUsersPage />} />
                        <Route path="properties" element={<AdminPropertiesPage />} />
                        <Route path="security" element={<AdminSecurityPage />} />
                        <Route path="audit-log" element={<AdminAuditLogPage />} />
                        <Route path="prelaunch" element={<AdminPrelaunchPage />} />
                        <Route path="settings" element={<AdminSettingsPage />} />
                      </Route>
                      {/* Legacy admin routes */}
                      <Route path="/admin-panel" element={<Navigate to="/admin" replace />} />
                      <Route path="/admin-pre-launch" element={<Navigate to="/admin/prelaunch" replace />} />
                      <Route path="/admin-settlement" element={<Navigate to="/admin/settlements" replace />} />
                      <Route path="/admin-analytics" element={<Navigate to="/admin/analytics" replace />} />
                      <Route path="/quiz-recommendations" element={
                        <ProtectedRoute requireAuth={true} allowedRoles={['student']}>
                          <RecommendedHostels />
                        </ProtectedRoute>
                      } />
                      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </RouteWrapper>
                    <Suspense fallback={null}>
                      <CompareFloatingBar />
                    </Suspense>
                    <Suspense fallback={null}>
                      <BottomNav />
                    </Suspense>
                </ComparisonProvider>
              </AuthGate>
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
      </>
    </Sentry.ErrorBoundary>
  );
};

export default App;
